import { db, auth } from "../config/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useEffect, useState, useCallback } from "react";
import { useGeoLocation } from "../components/useGeoLocation";
import {
  collection,
  query,
  orderBy,
  startAt,
  endAt,
  where,
  getDocs,
  limit as fbLimit,
  addDoc,
  serverTimestamp,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import { geohashQueryBounds, distanceBetween } from "geofire-common";
import Modal from "react-modal";
import { useNavigate, useLocation } from "react-router-dom";
import SafeMedia from "../components/SafeMedia";
import { ClipLoader } from "react-spinners";
import { useListenStatus } from "../hooks/useListenToRTDB";

// Helper to fetch users in chunks of 10 (Firestore 'in' query limit)
const fetchUsersByAuthUIDs = async (uids) => {
  if (!uids || uids.length === 0) return [];
  
  // Remove duplicates and empty values
  const uniqueUids = [...new Set(uids.filter(Boolean))];
  
  const chunk = (arr, n) =>
    arr.reduce(
      (acc, _, i) => (i % n ? acc : [...acc, arr.slice(i, i + n)]),
      []
    );

  const chunks = chunk(uniqueUids, 10);
  const users = [];

  for (const c of chunks) {
    const q = query(collection(db, "users"), where("authUID", "in", c));
    const snap = await getDocs(q);
    snap.docs.forEach((doc) => users.push({ id: doc.id, ...doc.data() }));
  }
  return users;
};

export const DiscoveryPage = () => {
  const [user] = useAuthState(auth);
  const { locationInfo, locationError } = useGeoLocation();
  const location = useLocation();
  const navigate = useNavigate();
  const statuses = useListenStatus();

  // --- State ---
  const [manualCoordinates] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [nearestUsers, setNearestUsers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [blockedUids, setBlockedUids] = useState([]);
  const [limit, setLimit] = useState(20);
  const [totalFiltered, setTotalFiltered] = useState(0);

  // Filters State
  const defaultFilters = {
    distanceKm: 50,
    ageMin: 18,
    ageMax: 99,
    role: "",
    meetAt: "",
    lookingFor: "",
    onlineOnly: false,
  };
  const [filters, setFilters] = useState(defaultFilters);
  const [form, setForm] = useState(defaultFilters);

  // Status/Modals State
  const [isLoading, setIsLoading] = useState(false);
  const [myIsPremium, setMyIsPremium] = useState(true);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);

  // View Mode State
  const [viewMode, setViewMode] = useState("nearby");
  const [viewerSearch, setViewerSearch] = useState("");
  const [nearbySearch, setNearbySearch] = useState("");
  
  // Lists State
  const [recentViewers, setRecentViewers] = useState([]);
  const [loadingViewers, setLoadingViewers] = useState(false);
  
  const [favourites, setFavourites] = useState([]);
  const [loadingFavourites, setLoadingFavourites] = useState(false);
  const [favouritedUsers, setFavouritedUsers] = useState([]);
  
  const [mostLikedUsers, setMostLikedUsers] = useState([]);
  const [loadingMostLiked, setLoadingMostLiked] = useState(false);
  
  const [myLikes, setMyLikes] = useState([]);
  const [loadingMyLikes, setLoadingMyLikes] = useState(false);
  const [likedUsers, setLikedUsers] = useState([]);

  const [timeRange, setTimeRange] = useState("week"); // 'week' or 'month'
  const [onlineUsersList, setOnlineUsersList] = useState([]);

  // --- Effects ---

  // Infinite Scroll Handler
  useEffect(() => {
    const handleScroll = () => {
      // If user scrolls within 500px of the bottom, increment the limit to show more profiles
      if (
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 500
      ) {
        setLimit((prev) => prev + 6);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Sync online status
  useEffect(() => {
    setOnlineUsersList(statuses);
  }, [statuses]);

  // Sync view mode from navigation state
  useEffect(() => {
    if (location.state?.viewMode) {
      setViewMode(location.state.viewMode);
    }
  }, [location.state]);

  // Fetch Current User Profile, Blocked List, and Interaction IDs
  useEffect(() => {
    if (!user) return;

    const initUserData = async () => {
      try {
        // 1. Fetch My Profile & Blocked List
        const meQ = query(collection(db, "users"), where("authUID", "==", user.uid));
        const meSnap = await getDocs(meQ);
        
        if (!meSnap.empty) {
          const userData = meSnap.docs[0].data();
          setUserProfile(userData);
          setBlockedUids(Array.isArray(userData.blocked) ? userData.blocked : []);
          setMyIsPremium(!!userData.isPremium);
        } else {
          setBlockedUids([]);
          setMyIsPremium(false);
        }

        // 2. Fetch My Likes (Who I liked)
        const likesQuery = query(collection(db, "likes"), where("likerUid", "==", user.uid));
        const likesSnapshot = await getDocs(likesQuery);
        setLikedUsers(likesSnapshot.docs.map((doc) => doc.data().likedUid));

        // 3. Fetch My Favourites (Who I favourited)
        const favouritesQuery = query(collection(db, "favourites"), where("favouriterUid", "==", user.uid));
        const favouritesSnapshot = await getDocs(favouritesQuery);
        setFavouritedUsers(favouritesSnapshot.docs.map((doc) => doc.data().favouritedUid));

      } catch (e) {
        console.error("Error initializing user data:", e);
      }
    };

    initUserData();
  }, [user]);

  // Reset premium filters if user is not premium
  useEffect(() => {
    if (myIsPremium) return;
    const reset = { role: "", meetAt: "", lookingFor: "", onlineOnly: false };
    setForm((f) => ({ ...f, ...reset }));
    setFilters((f) => ({ ...f, ...reset }));
  }, [myIsPremium]);

  // --- Core Logic: Fetch Nearby Users ---
  useEffect(() => {
    const loc = userProfile?.location || locationInfo;
    if (!user || !loc || viewMode !== 'nearby') return;
    
    // Ensure we have coordinates
    const lat = typeof loc === 'object' ? loc.latitude : null;
    const lng = typeof loc === 'object' ? loc.longitude : null;
    
    if (lat == null || lng == null) return;

    setIsLoading(true);

    const fetchNearestUsers = async () => {
      const center = [lat, lng];
      const radiusInM = (filters.distanceKm ?? 50) * 1000;
      const raw = [];

      try {
        if (filters.distanceKm >= 5000) {
          // For huge distances, bypass geohashQueryBounds to prevent missing users
          const q = query(collection(db, "users"));
          const snap = await getDocs(q);
          snap.docs.forEach((d) => {
            const data = d.data();
            const l = data?.location;
            if (!l || !l.latitude || !l.longitude) return;
            if (data.authUID === user.uid) return;
            if (blockedUids.includes(data.authUID)) return;
            if (Array.isArray(data.blocked) && data.blocked.includes(user.uid)) return;

            const distKm = distanceBetween([l.latitude, l.longitude], center);
            if (distKm <= filters.distanceKm) {
              raw.push({ id: d.id, ...data, distance: distKm });
            }
          });
        } else {
          const bounds = geohashQueryBounds(center, radiusInM);
          const promises = bounds.map(([start, end]) => {
            const q = query(
              collection(db, "users"),
              orderBy("location.geohash"),
              startAt(start),
              endAt(end)
            );
            return getDocs(q);
          });

          const snapshots = await Promise.all(promises);
          snapshots.forEach((snap) => {
            snap.docs.forEach((d) => {
              const data = d.data();
              const l = data?.location;
              if (!l || !l.latitude || !l.longitude) return;
              if (data.authUID === user.uid) return;
              if (blockedUids.includes(data.authUID)) return;
              if (Array.isArray(data.blocked) && data.blocked.includes(user.uid)) return;

              const distKm = distanceBetween([l.latitude, l.longitude], center);
              if (distKm <= filters.distanceKm) {
                raw.push({ id: d.id, ...data, distance: distKm });
              }
            });
          });
        }
      } catch (err) {
        console.error("Error fetching nearest users:", err);
      }

      // Deduplicate by authUID
      const seen = new Set();
      const deduped = [];
      for (const r of raw) {
        if (!seen.has(r.authUID)) {
          seen.add(r.authUID);
          deduped.push(r);
        }
      }

      setCandidates(deduped);
      setLimit(6); // Start limit at 6 as requested
      setIsLoading(false);
    };

    fetchNearestUsers();
  }, [user, locationInfo, filters.distanceKm, blockedUids, userProfile, viewMode]);

  // --- Helpers ---
  const passesFilters = useCallback(
    (u) => {
      const age = Number(u.age); // Convert to number safely
      if (!isNaN(age)) {
        if (age < filters.ageMin || age > filters.ageMax) return false;
      }

      // if (filters.onlineOnly && !u.isOnline) return false;
      if (filters.onlineOnly) {
      const isCurrentlyOnline = onlineUsersList?.[u.authUID]?.isOnline ?? false;
      if (!isCurrentlyOnline) return false;
    }
      if (filters.role && u.role !== filters.role) return false;

      if (filters.meetAt) {
        const prefs = Array.isArray(u.locationPref) ? u.locationPref : [];
        if (!prefs.includes(filters.meetAt)) return false;
      }

      if (filters.lookingFor) {
        const lf = Array.isArray(u.lookingFor) ? u.lookingFor : [];
        if (!lf.includes(filters.lookingFor)) return false;
      }

      return true;
    },
    [filters]
  );

  const filterAndMapUser = (u, extraData = {}) => {
    // Shared blocking logic check
    if (blockedUids.includes(u.authUID)) return null;
    if (Array.isArray(u.blocked) && u.blocked.includes(user.uid)) return null;

    if (!passesFilters(u)) return null;

    return {
      displayName: u.displayName,
      id: u.id,
      uid: u.authUID,
      age: u.age,
      distance: u.distance, // Only present in nearby
      pronouns: u.pronouns,
      sexuality: u.sexuality,
      online: u.isOnline,
      role: u.role,
      displayPhoto: u.displayPhoto,
      isStealth: u.isStealth,
      ...extraData,
    };
  };

  // --- View Mode Effects ---

  // 1. Process Nearby Candidates
  useEffect(() => {
    if (viewMode !== "nearby") return;
    
    const all = candidates
      .filter(passesFilters)
      .sort((a, b) => a.distance - b.distance);

    const filtered = nearbySearch
      ? all.filter((u) => u.displayName?.toLowerCase().includes(nearbySearch.toLowerCase()))
      : all;

    setTotalFiltered(filtered.length);
    setNearestUsers(filtered.slice(0, limit).map(u => filterAndMapUser(u)));
  }, [candidates, limit, viewMode, passesFilters, nearbySearch]);

  // 2. Fetch Viewers
  useEffect(() => {
    if (!user || viewMode !== "viewers") return;

    const run = async () => {
      setLoadingViewers(true);
      try {
        const pvQ = query(
          collection(db, "profileViews"),
          where("targetUid", "==", user.uid),
          // where("reportedUID", "==", user.uid),
          orderBy("viewedAt", "desc"),
          fbLimit(100)
        );
        const pvSnap = await getDocs(pvQ);
        const rows = pvSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        
        const uids = rows.map((r) => r.viewerUid);
        const users = await fetchUsersByAuthUIDs(uids);

        const viewers = rows
          .map((r) => {
            const u = users.find((x) => x.authUID === r.viewerUid);
            if (!u) return null;
            return filterAndMapUser(u, { 
                viewedAt: r.viewedAt?.toDate ? r.viewedAt.toDate() : null 
            });
          })
          .filter(Boolean);

        // Client-side search
        const filtered = viewerSearch
          ? viewers.filter((v) => v.displayName?.toLowerCase().includes(viewerSearch.toLowerCase()))
          : viewers;

        setRecentViewers(filtered);
      } catch (e) {
        console.error("fetch recent viewers error", e);
        setRecentViewers([]);
      } finally {
        setLoadingViewers(false);
      }
    };

    run();
  }, [user, viewMode, viewerSearch, blockedUids, passesFilters]);

  // 3. Fetch Favourites
  useEffect(() => {
    if (!user || viewMode !== "favourites") return;

    const run = async () => {
      setLoadingFavourites(true);
      try {
        const favQ = query(collection(db, "favourites"), where("favouriterUid", "==", user.uid));
        const favSnap = await getDocs(favQ);
        const uids = favSnap.docs.map((d) => d.data().favouritedUid);

        const users = await fetchUsersByAuthUIDs(uids);
        
        const favs = users
            .map(u => filterAndMapUser(u))
            .filter(Boolean);

        setFavourites(favs);
      } catch (e) {
        console.error("fetch favourites error", e);
        setFavourites([]);
      } finally {
        setLoadingFavourites(false);
      }
    };
    run();
  }, [user, viewMode, blockedUids, passesFilters]);

  // 4. Fetch Most Liked
  useEffect(() => {
    if (!user || viewMode !== "most-liked") return;

    const run = async () => {
      setLoadingMostLiked(true);
      try {
        const startDate = new Date();
        if (timeRange === "week") startDate.setDate(startDate.getDate() - 7);
        else startDate.setMonth(startDate.getMonth() - 1);

        const likesQ = query(collection(db, "likes"), where("createdAt", ">=", startDate));
        const likesSnap = await getDocs(likesQ);

        const likeCounts = likesSnap.docs.reduce((acc, doc) => {
          const { likedUid } = doc.data();
          acc[likedUid] = (acc[likedUid] || 0) + 1;
          return acc;
        }, {});

        // Sort UIDs by count
        const sortedUids = Object.entries(likeCounts)
          .sort(([, a], [, b]) => b - a)
          .map(([uid]) => uid);

        const users = await fetchUsersByAuthUIDs(sortedUids);

        const likedProfiles = users
          .map((u) => filterAndMapUser(u, { likeCount: likeCounts[u.authUID] || 0 }))
          .filter(Boolean)
          .sort((a, b) => b.likeCount - a.likeCount);

        setMostLikedUsers(likedProfiles);
      } catch (e) {
        console.error("fetch most liked error", e);
        setMostLikedUsers([]);
      } finally {
        setLoadingMostLiked(false);
      }
    };
    run();
  }, [user, viewMode, timeRange, blockedUids, passesFilters]);

  // 5. Fetch My Likes (Incoming)
  useEffect(() => {
    if (!user || viewMode !== "my-likes") return;

    const run = async () => {
      setLoadingMyLikes(true);
      try {
        const likesQ = query(collection(db, "likes"), where("likedUid", "==", user.uid));
        const likesSnap = await getDocs(likesQ);
        
        const likerInfo = likesSnap.docs.map((d) => ({
          uid: d.data().likerUid,
          createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate() : null,
        }));

        const uids = likerInfo.map(l => l.uid);
        const users = await fetchUsersByAuthUIDs(uids);

        const likes = users
          .map((u) => {
            const info = likerInfo.find(l => l.uid === u.authUID);
            return filterAndMapUser(u, { likedAt: info ? info.createdAt : null });
          })
          .filter(Boolean);

        setMyLikes(likes);
      } catch (e) {
        console.error("fetch my likes error", e);
        setMyLikes([]);
      } finally {
        setLoadingMyLikes(false);
      }
    };
    run();
  }, [user, viewMode, blockedUids, passesFilters]);

  // --- Handlers ---

  const formatRelative = (d) => {
    if (!d) return "recently";
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min${m === 1 ? "" : "s"} ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
    const days = Math.floor(h / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  };

  const handleViewProfile = (uid) => {
    navigate(`/profile/${uid}`);
  };

  // REFINED LIKE HANDLER (Fixes the toggle bug)
  const handleLike = async (likedUid) => {
    if (!user) return;

    try {
      // Check if already liked
      const q = query(
        collection(db, "likes"),
        where("likerUid", "==", user.uid),
        where("likedUid", "==", likedUid)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        // UNLIKE: Delete the existing like
        const docToDelete = snapshot.docs[0];
        await deleteDoc(docToDelete.ref);
        
        setLikedUsers((prev) => prev.filter((id) => id !== likedUid));
        setSaveNotice({ type: "success", msg: "Unliked profile." });
      } else {
        // LIKE: Add new like
        await addDoc(collection(db, "likes"), {
          likerUid: user.uid,
          likedUid: likedUid,
          createdAt: serverTimestamp(),
        });
        
        setLikedUsers((prev) => [...prev, likedUid]);
        setSaveNotice({ type: "success", msg: "You've liked this profile!" });
      }
      setTimeout(() => setSaveNotice(null), 3000);
    } catch (error) {
      console.error("Error toggling like:", error);
      setSaveNotice({
        type: "error",
        msg: "Error updating like status. Please try again.",
      });
      setTimeout(() => setSaveNotice(null), 3000);
    }
  };

  const handleFavourite = async (favouritedUid) => {
    if (!user) return;

    try {
      const q = query(
        collection(db, "favourites"),
        where("favouriterUid", "==", user.uid),
        where("favouritedUid", "==", favouritedUid)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        await addDoc(collection(db, "favourites"), {
          favouriterUid: user.uid,
          favouritedUid: favouritedUid,
          createdAt: serverTimestamp(),
        });
        setFavouritedUsers((prev) => [...prev, favouritedUid]);
        setSaveNotice({ type: "success", msg: "You've favourited this profile!" });
      } else {
        const docToDelete = snapshot.docs[0];
        await deleteDoc(docToDelete.ref);
        setFavouritedUsers((prev) => prev.filter((uid) => uid !== favouritedUid));
        setSaveNotice({ type: "success", msg: "You've unfavourited this profile." });
      }
      setTimeout(() => setSaveNotice(null), 3000);
    } catch (error) {
      console.error("Error updating favourite status:", error);
      setSaveNotice({ type: "error", msg: "Error updating favourites. Try again." });
      setTimeout(() => setSaveNotice(null), 3000);
    }
  };

  // Filter Logic
  const applyFilters = () => {
    if (myIsPremium) {
      setFilters(form);
    } else {
      setFilters((f) => ({
        ...f,
        ageMin: form.ageMin,
        ageMax: form.ageMax,
        distanceKm: form.distanceKm,
        // Reset locked filters
        role: "",
        meetAt: "",
        lookingFor: "",
        onlineOnly: false,
      }));
    }
  };

  const resetFilters = () => {
    setForm(defaultFilters);
    setFilters(defaultFilters);
  };

  const updatePremiumForm = (field, value) => {
    if (!myIsPremium) {
      setShowPremiumModal(true);
      return;
    }
    setForm((f) => ({ ...f, [field]: value }));
  };

  const ageOptions = [
    { label: "Any", value: "Any" },
    { label: "18–25", value: "18-25" },
    { label: "26–35", value: "26-35" },
    { label: "36–45", value: "36-45" },
    { label: "46+", value: "46-99" },
  ];

  // const distanceOptions = [5, 10, 25, 50, 100, 250, 500, 2000, 10000, 100000];
  const distanceOptions = [
  { label: 5, value: 5 },
  { label: 10, value: 10 },
  { label: 25, value: 25 },
  { label: 50, value: 50 },
  { label: 100, value: 100 },
  { label: 250, value: 250 },
  { label: 500, value: 500 },
  { label: 2000, value: 2000 },
  { label: 10000, value: 10000 },
  { label: "Any", value: 100000 }
]

  const formatDistance = (km) =>
    km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;

  // --- Render ---
  return (
    <div className="main discovery">
      <div className="content">
        {saveNotice && (
          <div
            role="status"
            aria-live="polite"
            className={`notice ${
              saveNotice.type === "success" ? "notice-success" : "notice-error"
            }`}
            style={{
              marginBottom: "12px",
              padding: "10px 12px",
              borderRadius: "8px",
              background:
                saveNotice.type === "success"
                  ? "rgba(16,185,129,0.15)"
                  : "rgba(239,68,68,0.15)",
              border: `1px solid ${
                saveNotice.type === "success"
                  ? "rgba(16,185,129,0.6)"
                  : "rgba(239,68,68,0.6)"
              }`,
            }}
          >
            {saveNotice.msg}
          </div>
        )}
        {user ? (
          <>
            {/* Premium upsell modal */}
            <Modal
              className="upgrade-prompt"
              isOpen={showPremiumModal}
              onRequestClose={() => setShowPremiumModal(false)}
              contentLabel="Upgrade to unlock filters"
            >
              <>
                <h2>Unlock more filters</h2>
                <p>
                  Sorting/filtering by Role, Meet At, Looking For, and Online Now
                  is a Premium feature. Upgrade to refine your matches.
                </p>
              </>
              <div className="actions">
                <button onClick={() => setShowPremiumModal(false)}>
                  Maybe later
                </button>
                <button onClick={() => setShowPremiumModal(false)}>
                  Upgrade
                </button>
              </div>
            </Modal>

            {/* Filters Row */}
            <section className="filters">
              <div className="filters-inner">
                <div className="filter-icon" />

                <div className="filter-item">
                  <label htmlFor="filter-age">Age</label>
                  <select
                    id="filter-age"
                    name="age"
                    value={`${form.ageMin}-${form.ageMax}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (!v || v === "Any") {
                        setForm((f) => ({ ...f, ageMin: 18, ageMax: 99 }));
                      } else {
                        const [min, max] = v.split("-").map(Number);
                        setForm((f) => ({ ...f, ageMin: min, ageMax: max }));
                      }
                    }}
                  >
                    {ageOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-item">
                  <label htmlFor="filter-distance">Distance</label>
                  <select
                    id="filter-distance"
                    name="distance"
                    value={form.distanceKm}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, distanceKm: Number(e.target.value) }))
                    }
                  >
                    {distanceOptions.map((km) => {
                      if(km.value === 100000){
                        return(
                          <option key={km.value} value={km.value}>
                            {km.label}
                          </option>
                        )
                      }
                      return(
                      <option key={km.value} value={km.value}>
                        {`≤ ${km.label} km`}
                      </option>
                    )})}
                  </select>
                </div>

                <div className="filter-item">
                  <label htmlFor="filter-role">Role</label>
                  <select
                    id="filter-role"
                    name="role"
                    value={form.role}
                    // disabled={!myIsPremium}
                    onChange={(e) => updatePremiumForm("role", e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="verse">Verse</option>
                    <option value="side">Side</option>
                  </select>
                </div>

                <div className="filter-item">
                  <label htmlFor="filter-meet">Meet At</label>
                  <select
                    id="filter-meet"
                    name="meet_at"
                    value={form.meetAt}
                    disabled={!myIsPremium}
                    onChange={(e) => updatePremiumForm("meetAt", e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="yours">Yours</option>
                    <option value="mine">Mine</option>
                    <option value="outside">Outside</option>
                    <option value="cafe">Cafe</option>
                    <option value="bar">Bar</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="filter-item">
                  <label htmlFor="filter-looking">Looking For</label>
                  <select
                    id="filter-looking"
                    name="looking_for"
                    value={form.lookingFor}
                    disabled={!myIsPremium}
                    onChange={(e) => updatePremiumForm("lookingFor", e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="friends">Friends</option>
                    <option value="dating">Dating</option>
                    <option value="casual">Casual</option>
                    <option value="relationship">Relationship</option>
                  </select>
                </div>

                <div className="filter-item">
                  <label htmlFor="filter-online">Online Now</label>
                  <select
                    id="filter-online"
                    name="online_now"
                    value={form.onlineOnly ? "1" : ""}
                    disabled={!myIsPremium}
                    onChange={(e) => updatePremiumForm("onlineOnly", e.target.value === "1")}
                  >
                    <option value="">All</option>
                    <option value="1">Online</option>
                  </select>
                </div>

                <div className="filter-actions">
                  <button className="btn-reset" type="button" onClick={resetFilters}>
                    Reset
                  </button>
                  <button className="btn-apply" type="button" onClick={applyFilters}>
                    Apply
                  </button>
                </div>
              </div>
            </section>

            {/* Results List */}
            <section className="results-list">
              {!locationInfo && locationError && !manualCoordinates && (
                <p className="notice">
                  We couldn’t get your location. Enable permissions or search for a location manually.
                </p>
              )}

              <div className="view-switcher">
                <button
                  className={viewMode === "nearby" ? "active" : ""}
                  onClick={() => setViewMode("nearby")}
                >
                  Nearby
                </button>
                <button
                  className={viewMode === "viewers" ? "active" : ""}
                  onClick={() => setViewMode("viewers")}
                >
                  Recent Views
                </button>
                <button
                  className={viewMode === "my-likes" ? "active" : ""}
                  onClick={() => setViewMode("my-likes")}
                >
                  Likes You
                </button>
                <button
                  className={viewMode === "favourites" ? "active" : ""}
                  onClick={() => setViewMode("favourites")}
                >
                  Favourites
                </button>
                <button
                  className={viewMode === "most-liked" ? "active" : ""}
                  onClick={() => setViewMode("most-liked")}
                >
                  Most Liked
                </button>
              </div>

              {viewMode === "most-liked" && (
                <div className="time-range-selector">
                  <button
                    className={timeRange === "week" ? "active" : ""}
                    onClick={() => setTimeRange("week")}
                  >
                    This Week
                  </button>
                  <button
                    className={timeRange === "month" ? "active" : ""}
                    onClick={() => setTimeRange("month")}
                  >
                    This Month
                  </button>
                </div>
              )}

              {viewMode === "viewers" && (
                <div className="viewer-search">
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={viewerSearch}
                    onChange={(e) => setViewerSearch(e.target.value)}
                  />
                </div>
              )}

              {viewMode === "nearby" && (
                <div className="viewer-search">
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={nearbySearch}
                    onChange={(e) => setNearbySearch(e.target.value)}
                  />
                </div>
              )}

              <p className="results-count">
                {viewMode === "nearby" &&
                  (isLoading ? (
                    <ClipLoader size={20} />
                  ) : (
                    <>Showing {nearestUsers.length} of {totalFiltered}</>
                  ))}
                {viewMode === "viewers" &&
                  (loadingViewers ? (
                    <ClipLoader size={20} />
                  ) : (
                    <>Showing {recentViewers.length} recent viewers</>
                  ))}
                {viewMode === "my-likes" &&
                  (loadingMyLikes ? (
                    <ClipLoader size={20} />
                  ) : (
                    <>Showing {myLikes.length} profiles that liked you</>
                  ))}
                {viewMode === "favourites" &&
                  (loadingFavourites ? (
                    <ClipLoader size={20} />
                  ) : (
                    <>Showing {favourites.length} favourited profiles</>
                  ))}
                {viewMode === "most-liked" &&
                  (loadingMostLiked ? (
                    <ClipLoader size={20} />
                  ) : (
                    <>Showing top {mostLikedUsers.length} profiles</>
                  ))}
              </p>

              <div className="profile-row">
                {(viewMode === "nearby"
                  ? nearestUsers
                  : viewMode === "viewers"
                  ? recentViewers
                  : viewMode === "my-likes"
                  ? myLikes
                  : viewMode === "most-liked"
                  ? mostLikedUsers
                  : favourites
                ).map((u) => {
                  const online = onlineUsersList?.[u?.uid]?.isOnline ?? false;
                  const isStealth = u?.isStealth ?? false;
         
                  if(isStealth){
                    return null;
                  }
       
                  return (
                    <div
                      className="profile-row-item"
                      key={u.uid}
                      onClick={() => handleViewProfile(u.uid)}
                    >
                      <div className="profile-image">
                        <SafeMedia
                          src={u.displayPhoto}
                          alt={`${u.displayName}'s photo`}
                          fallback="/img/placeholder.jpg"
                        />
                      </div>
                      <div className="profile-info">
                        <div className="user-info">
                          <span
                            className={`online-indicator ${
                              online ? "is-online" : ""
                            }`}
                            aria-label={online ? "Online now" : "Offline"}
                            title={online ? "Online now" : "Offline"}
                          />
                          <span className="name">{u.displayName}</span>
                          <span className="age">{u.age}</span>
                          {viewMode === "nearby" && u.distance != null && (
                            <span className="distance">{` ${formatDistance(u.distance)} away`}</span>
                          )}
                          {viewMode === "viewers" && u.viewedAt && (
                            <span className="viewed-at">
                              {formatRelative(u.viewedAt)}
                            </span>
                          )}
                          {viewMode === "my-likes" && u.likedAt && (
                            <span className="liked-at">
                              {formatRelative(u.likedAt)}
                            </span>
                          )}
                          {viewMode === "most-liked" && (
                            <span className="like-count">❤️ {u.likeCount}</span>
                          )}
                        </div>
                        <div className="user-tags">
                          <span className="tag pronouns">{u.pronouns}</span>
                          <span className="tag sexuality">{u.sexuality}</span>
                          <span className="tag role">{u.role}</span>
                        </div>
                      </div>
                      <div
                        className={`like-button ${
                          likedUsers.includes(u.uid) ? "liked" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLike(u.uid);
                        }}
                      >
                        ❤️
                      </div>
                      <div
                        className={`favourite-button ${
                          favouritedUsers.includes(u.uid) ? "favourited" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFavourite(u.uid);
                        }}
                      >
                        ⭐
                      </div>
                    </div>
                  );
                })}
                {viewMode === "most-liked" &&
                  !loadingMostLiked &&
                  mostLikedUsers.length === 0 && (
                    <p className="notice">
                      No liked profiles found for this period.
                    </p>
                  )}
              </div>

              {viewMode === "nearby" && totalFiltered > nearestUsers.length && (
                <button
                  className="btn-load-more"
                  type="button"
                  onClick={() => setLimit((n) => n + 20)}
                >
                  See More Profiles
                </button>
              )}
            </section>
          </>
        ) : (
          <p>No profile found.</p>
        )}
      </div>
    </div>
  );
};

export default DiscoveryPage;


// import { db, auth } from "../config/firebase";
// import { useAuthState } from "react-firebase-hooks/auth";
// import { useEffect, useState, useCallback } from "react";
// import { useGeoLocation } from "../components/useGeoLocation";
// import {
//   collection,
//   query,
//   orderBy,
//   startAt,
//   endAt,
//   where,
//   getDocs,
//   limit as fbLimit,
//   addDoc,
//   serverTimestamp,
//   deleteDoc,
// } from "firebase/firestore";
// import { geohashQueryBounds, distanceBetween } from "geofire-common";
// import Modal from "react-modal";
// import { useNavigate } from "react-router-dom";
// import SafeMedia from "../components/SafeMedia";
// import { ClipLoader } from "react-spinners";
// import { useLocation } from "react-router-dom";
// import { useListenStatus } from "../hooks/useListenToRTDB";

// export const DiscoveryPage = () => {
//   const [user] = useAuthState(auth);
//   const { locationInfo, locationError } = useGeoLocation();
//   const location = useLocation();
//   const [manualCoordinates] = useState(null);
//   const [userProfile, setUserProfile] = useState(null);

//   const [nearestUsers, setNearestUsers] = useState([]);

//   const [candidates, setCandidates] = useState([]);

//   const [blockedUids, setBlockedUids] = useState([]);

//   const [limit, setLimit] = useState(20);

//   const [totalFiltered, setTotalFiltered] = useState(0);

//   const navigate = useNavigate();

//   const defaultFilters = {
//     distanceKm: 50,
//     ageMin: 18,
//     ageMax: 99,
//     role: "",
//     meetAt: "",
//     lookingFor: "",
//     onlineOnly: false,
//   };

//   const [filters, setFilters] = useState(defaultFilters);
//   const [form, setForm] = useState(defaultFilters);

//   const [isLoading, setIsLoading] = useState(false);

//   const [myIsPremium, setMyIsPremium] = useState(true);
//   const [showPremiumModal, setShowPremiumModal] = useState(false);

//   const [viewMode, setViewMode] = useState("nearby");
//   const [viewerSearch, setViewerSearch] = useState("");
//   const [nearbySearch, setNearbySearch] = useState("");
//   const [recentViewers, setRecentViewers] = useState([]);
//   const [loadingViewers, setLoadingViewers] = useState(false);
//   const [likedUsers, setLikedUsers] = useState([]);
//   const [favouritedUsers, setFavouritedUsers] = useState([]);
//   const [favourites, setFavourites] = useState([]);
//   const [loadingFavourites, setLoadingFavourites] = useState(false);
//   const [saveNotice, setSaveNotice] = useState(null);
//   const [mostLikedUsers, setMostLikedUsers] = useState([]);
//   const [loadingMostLiked, setLoadingMostLiked] = useState(false);
//   const [myLikes, setMyLikes] = useState([]);
//   const [loadingMyLikes, setLoadingMyLikes] = useState(false);
//   const [timeRange, setTimeRange] = useState("week"); // 'week' or 'month'
//   const [onlineUsersList, setOnlineUsersList] = useState([]);

//   const statuses = useListenStatus();

//   useEffect(() => {
//     setOnlineUsersList(statuses)
//   }, [statuses]);

//   useEffect(() => {
//     if (location.state?.viewMode) {
//       setViewMode(location.state.viewMode);
//     }
//   }, [location.state]);

//   useEffect(() => {
//     if (!user) return;

//     const fetchUserProfile = async () => {
//       try {
//         const meQ = query(
//           collection(db, "users"),
//           where("authUID", "==", user.uid)
//         );
//         const meSnap = await getDocs(meQ);
//         if (!meSnap.empty) {
//           const userData = meSnap.docs[0].data();
//           setUserProfile(userData);
//         }
//       } catch (e) {
//         console.error("fetchUserProfile error:", e);
//       }
//     };

//     fetchUserProfile();

//     const fetchUserInteractions = async () => {
//       // Fetch likes
//       const likesQuery = query(
//         collection(db, "likes"),
//         where("likerUid", "==", user.uid)
//       );
//       const likesSnapshot = await getDocs(likesQuery);
//       const likedUids = likesSnapshot.docs.map((doc) => doc.data().likedUid);
//       setLikedUsers(likedUids);

//       // Fetch favourites
//       const favouritesQuery = query(
//         collection(db, "favourites"),
//         where("favouriterUid", "==", user.uid)
//       );
//       const favouritesSnapshot = await getDocs(favouritesQuery);
//       const favouritedUids = favouritesSnapshot.docs.map(
//         (doc) => doc.data().favouritedUid
//       );
//       setFavouritedUsers(favouritedUids);
//     };

//     fetchUserInteractions();

//     (async () => {
//       try {
//         const meQ = query(
//           collection(db, "users"),
//           where("authUID", "==", user.uid)
//         );
//         const meSnap = await getDocs(meQ);
//         if (!meSnap.empty) {
//           const data = meSnap.docs[0].data() || {};
//           setBlockedUids(Array.isArray(data.blocked) ? data.blocked : []);
//           setMyIsPremium(!!data.isPremium);
//         } else {
//           setBlockedUids([]);
//           setMyIsPremium(false);
//         }
//       } catch (e) {
//         console.error("load blocked list error", e);
//         setBlockedUids([]);
//         setMyIsPremium(false);
//       }
//     })();
//   }, [user]);

//   useEffect(() => {
//     if (myIsPremium) return;
//     setForm((f) => ({
//       ...f,
//       role: "",
//       meetAt: "",
//       lookingFor: "",
//       onlineOnly: false,
//     }));
//     setFilters((f) => ({
//       ...f,
//       role: "",
//       meetAt: "",
//       lookingFor: "",
//       onlineOnly: false,
//     }));
//   }, [myIsPremium]);

//   useEffect(() => {
//     const location = userProfile?.location || locationInfo;
//     if (!user || !location) return;
//     setIsLoading(true);

//     const fetchNearestUsers = async () => {
//       const center = [location.latitude, location.longitude];
//       const radiusInM = (filters.distanceKm ?? 50) * 1000; // km -> m

//       const bounds = geohashQueryBounds(center, radiusInM);
//       const promises = bounds.map(([start, end]) => {
//         const q = query(
//           collection(db, "users"),
//           orderBy("location.geohash"),
//           startAt(start),
//           endAt(end)
//         );
//         return getDocs(q);
//       });

//       const snapshots = await Promise.all(promises);
//       const raw = [];

//       snapshots.forEach((snap) => {
//         snap.docs.forEach((d) => {
//           const data = d.data();
//           const loc = data?.location;
//           if (
//             !loc ||
//             typeof loc.latitude !== "number" ||
//             typeof loc.longitude !== "number" ||
//             !loc.geohash
//           ) {
//             return;
//           }

//           if (data.authUID === user.uid) return;

//           if (Array.isArray(blockedUids) && blockedUids.includes(data.authUID))
//             return;

//           if (Array.isArray(data.blocked) && data.blocked.includes(user.uid))
//             return;

//           const distKm = distanceBetween([loc.latitude, loc.longitude], center);

//           if (distKm <= filters.distanceKm) {
//             raw.push({ id: d.id, ...data, distance: distKm }); // store km
//           }
//         });
//       });

//       const seen = new Set();
//       const deduped = [];
//       for (const r of raw) {
//         if (!seen.has(r.authUID)) {
//           seen.add(r.authUID);
//           deduped.push(r);
//         }
//       }

//       setCandidates(deduped);
//       setLimit(20);
//       setIsLoading(false);
//     };

//     fetchNearestUsers();
//   }, [
//     user,
//     locationInfo,
//     manualCoordinates,
//     filters.distanceKm,
//     blockedUids,
//     userProfile,
//   ]);

//   const passesFilters = useCallback(
//     (u) => {
//       if (typeof u.age === "number") {
//         if (u.age < filters.ageMin || u.age > filters.ageMax) return false;
//       }

//       if (filters.onlineOnly && !u.isOnline) return false;

//       if (filters.role && u.role !== filters.role) return false;

//       if (filters.meetAt) {
//         const prefs = Array.isArray(u.locationPref) ? u.locationPref : [];
//         if (!prefs.includes(filters.meetAt)) return false;
//       }

//       if (filters.lookingFor) {
//         const lf = Array.isArray(u.lookingFor) ? u.lookingFor : [];
//         if (!lf.includes(filters.lookingFor)) return false;
//       }

//       return true;
//     },
//     [filters]
//   );

//   useEffect(() => {
//     if (viewMode !== "nearby") return;
//     const all = candidates
//       .filter(passesFilters)
//       .sort((a, b) => a.distance - b.distance);

//     const filtered = nearbySearch
//       ? all.filter((u) =>
//           u.displayName?.toLowerCase().includes(nearbySearch.toLowerCase())
//         )
//       : all;

//     setTotalFiltered(filtered.length);

//     const page = filtered.slice(0, limit).map((u) => ({
//       displayName: u.displayName,
//       id: u.id,
//       uid: u.authUID,
//       age: u.age,
//       distance: u.distance,
//       pronouns: u.pronouns,
//       sexuality: u.sexuality,
//       online: u.isOnline,
//       role: u.role,
//       displayPhoto: u.displayPhoto,
//     }));

//     setNearestUsers(page);
//   }, [candidates, limit, viewMode, passesFilters, nearbySearch]);

//   useEffect(() => {
//     if (!user || viewMode !== "viewers") return;

//     const run = async () => {
//       setLoadingViewers(true);
//       try {
//         // 1) Get my most recent view records
//         const pvQ = query(
//           collection(db, "profileViews"),
//           where("targetUid", "==", user.uid),
//           orderBy("viewedAt", "desc"),
//           fbLimit(100)
//         );
//         const pvSnap = await getDocs(pvQ);
//         const rows = pvSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
//         const uniqueViewerUids = Array.from(
//           new Set(rows.map((r) => r.viewerUid).filter(Boolean))
//         );

//         // 2) Fetch those users in chunks of 10 (limit of 'in' queries)
//         if (uniqueViewerUids.length === 0) {
//           setRecentViewers([]);
//           setLoadingViewers(false);
//           return;
//         }
//         const chunk = (arr, n) =>
//           arr.reduce(
//             (acc, _, i) => (i % n ? acc : [...acc, arr.slice(i, i + n)]),
//             []
//           );
//         const chunks = chunk(uniqueViewerUids, 10);

//         const users = [];
//         for (const c of chunks) {
//           const uQ = query(collection(db, "users"), where("authUID", "in", c));
//           const uSnap = await getDocs(uQ);
//           uSnap.docs.forEach((doc) =>
//             users.push({ id: doc.id, ...doc.data() })
//           );
//         }

//         // 3) Map back to viewer list, respecting blocks
//         const me = user.uid;
//         const blockedSet = new Set(blockedUids);
//         const viewers = rows
//           .map((r) => {
//             const u = users.find((x) => x.authUID === r.viewerUid);
//             if (!u) return null;
//             if (blockedSet.has(u.authUID)) return null; // I blocked them
//             if (Array.isArray(u.blocked) && u.blocked.includes(me)) return null; // they blocked me
//             return {
//               displayName: u.displayName,
//               id: u.id,
//               uid: u.authUID,
//               age: u.age,
//               pronouns: u.pronouns,
//               sexuality: u.sexuality,
//               online: u.isOnline,
//               role: u.role,
//               displayPhoto: u.displayPhoto,
//               viewedAt: r.viewedAt?.toDate ? r.viewedAt.toDate() : null,
//             };
//           })
//           .filter(Boolean)
//           .filter(passesFilters);

//         // 4) Optional client-side search by display name
//         const filtered = viewerSearch
//           ? viewers.filter((v) =>
//               v.displayName?.toLowerCase().includes(viewerSearch.toLowerCase())
//             )
//           : viewers;

//         setRecentViewers(filtered);
//       } catch (e) {
//         console.error("fetch recent viewers error", e);
//         setRecentViewers([]);
//       } finally {
//         setLoadingViewers(false);
//       }
//     };

//     run();
//   }, [user, viewMode, viewerSearch, blockedUids, passesFilters]);

//   useEffect(() => {
//     if (!user || viewMode !== "favourites") return;

//     const run = async () => {
//       setLoadingFavourites(true);
//       try {
//         const favQ = query(
//           collection(db, "favourites"),
//           where("favouriterUid", "==", user.uid)
//         );
//         const favSnap = await getDocs(favQ);
//         const favouritedUids = favSnap.docs.map((d) => d.data().favouritedUid);

//         if (favouritedUids.length === 0) {
//           setFavourites([]);
//           setLoadingFavourites(false);
//           return;
//         }

//         const chunk = (arr, n) =>
//           arr.reduce(
//             (acc, _, i) => (i % n ? acc : [...acc, arr.slice(i, i + n)]),
//             []
//           );
//         const chunks = chunk(favouritedUids, 10);

//         const users = [];
//         for (const c of chunks) {
//           const uQ = query(collection(db, "users"), where("authUID", "in", c));
//           const uSnap = await getDocs(uQ);
//           uSnap.docs.forEach((doc) =>
//             users.push({ id: doc.id, ...doc.data() })
//           );
//         }

//         const me = user.uid;
//         const blockedSet = new Set(blockedUids);
//         const favs = users
//           .map((u) => {
//             if (blockedSet.has(u.authUID)) return null;
//             if (Array.isArray(u.blocked) && u.blocked.includes(me)) return null;
//             return {
//               displayName: u.displayName,
//               id: u.id,
//               uid: u.authUID,
//               age: u.age,
//               pronouns: u.pronouns,
//               sexuality: u.sexuality,
//               online: u.isOnline,
//               role: u.role,
//               displayPhoto: u.displayPhoto,
//             };
//           })
//           .filter(Boolean)
//           .filter(passesFilters);

//         setFavourites(favs);
//       } catch (e) {
//         console.error("fetch favourites error", e);
//         setFavourites([]);
//       } finally {
//         setLoadingFavourites(false);
//       }
//     };

//     run();
//   }, [user, viewMode, blockedUids, passesFilters]);

//   useEffect(() => {
//     if (!user || viewMode !== "most-liked") return;

//     const run = async () => {
//       setLoadingMostLiked(true);
//       try {
//         const now = new Date();
//         const startDate = new Date();
//         if (timeRange === "week") {
//           startDate.setDate(now.getDate() - 7);
//         } else {
//           // month
//           startDate.setMonth(now.getMonth() - 1);
//         }

//         const likesQ = query(
//           collection(db, "likes"),
//           where("createdAt", ">=", startDate)
//         );
//         const likesSnap = await getDocs(likesQ);

//         const likeCounts = likesSnap.docs.reduce((acc, doc) => {
//           const { likedUid } = doc.data();
//           acc[likedUid] = (acc[likedUid] || 0) + 1;
//           return acc;
//         }, {});

//         const sortedUids = Object.entries(likeCounts)
//           .sort(([, a], [, b]) => b - a)
//           .map(([uid]) => uid);

//         if (sortedUids.length === 0) {
//           setMostLikedUsers([]);
//           setLoadingMostLiked(false);
//           return;
//         }

//         const chunk = (arr, n) =>
//           arr.reduce(
//             (acc, _, i) => (i % n ? acc : [...acc, arr.slice(i, i + n)]),
//             []
//           );
//         const chunks = chunk(sortedUids, 10);

//         const users = [];
//         for (const c of chunks) {
//           const uQ = query(collection(db, "users"), where("authUID", "in", c));
//           const uSnap = await getDocs(uQ);
//           uSnap.docs.forEach((doc) =>
//             users.push({ id: doc.id, ...doc.data() })
//           );
//         }

//         const me = user.uid;
//         const blockedSet = new Set(blockedUids);
//         const likedProfiles = users
//           .map((u) => {
//             if (blockedSet.has(u.authUID)) return null;
//             if (Array.isArray(u.blocked) && u.blocked.includes(me)) return null;
//             return {
//               displayName: u.displayName,
//               id: u.id,
//               uid: u.authUID,
//               age: u.age,
//               pronouns: u.pronouns,
//               sexuality: u.sexuality,
//               online: u.isOnline,
//               role: u.role,
//               displayPhoto: u.displayPhoto,
//               likeCount: likeCounts[u.authUID] || 0,
//             };
//           })
//           .filter(Boolean)
//           .filter(passesFilters)
//           .sort((a, b) => b.likeCount - a.likeCount);

//         setMostLikedUsers(likedProfiles);
//       } catch (e) {
//         console.error("fetch most liked error", e);
//         setMostLikedUsers([]);
//       } finally {
//         setLoadingMostLiked(false);
//       }
//     };

//     run();
//   }, [user, viewMode, timeRange, blockedUids, passesFilters]);

//   useEffect(() => {
//     if (!user || viewMode !== "my-likes") return;

//     const run = async () => {
//       setLoadingMyLikes(true);
//       try {
//         const likesQ = query(
//           collection(db, "likes"),
//           where("likedUid", "==", user.uid)
//         );
//         const likesSnap = await getDocs(likesQ);
//         const likerUids = likesSnap.docs.map((d) => ({
//           uid: d.data().likerUid,
//           createdAt: d.data().createdAt?.toDate
//             ? d.data().createdAt.toDate()
//             : null,
//         }));

//         if (likerUids.length === 0) {
//           setMyLikes([]);
//           setLoadingMyLikes(false);
//           return;
//         }

//         const uids = likerUids.map((l) => l.uid);
//         const chunk = (arr, n) =>
//           arr.reduce(
//             (acc, _, i) => (i % n ? acc : [...acc, arr.slice(i, i + n)]),
//             []
//           );
//         const chunks = chunk(uids, 10);

//         const users = [];
//         for (const c of chunks) {
//           const uQ = query(collection(db, "users"), where("authUID", "in", c));
//           const uSnap = await getDocs(uQ);
//           uSnap.docs.forEach((doc) =>
//             users.push({ id: doc.id, ...doc.data() })
//           );
//         }

//         const me = user.uid;
//         const blockedSet = new Set(blockedUids);
//         const likes = users
//           .map((u) => {
//             if (blockedSet.has(u.authUID)) return null;
//             if (Array.isArray(u.blocked) && u.blocked.includes(me)) return null;
//             const likeInfo = likerUids.find((l) => l.uid === u.authUID);
//             return {
//               displayName: u.displayName,
//               id: u.id,
//               uid: u.authUID,
//               age: u.age,
//               pronouns: u.pronouns,
//               sexuality: u.sexuality,
//               online: u.isOnline,
//               role: u.role,
//               displayPhoto: u.displayPhoto,
//               likedAt: likeInfo ? likeInfo.createdAt : null,
//             };
//           })
//           .filter(Boolean)
//           .filter(passesFilters);

//         setMyLikes(likes);
//       } catch (e) {
//         console.error("fetch my likes error", e);
//         setMyLikes([]);
//       } finally {
//         setLoadingMyLikes(false);
//       }
//     };

//     run();
//   }, [user, viewMode, blockedUids, passesFilters]);

//   const formatRelative = (d) => {
//     if (!d) return "recently";
//     const s = Math.floor((Date.now() - d.getTime()) / 1000);
//     if (s < 60) return "just now";
//     const m = Math.floor(s / 60);
//     if (m < 60) return `${m} min${m === 1 ? "" : "s"} ago`;
//     const h = Math.floor(m / 60);
//     if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
//     const days = Math.floor(h / 24);
//     return `${days} day${days === 1 ? "" : "s"} ago`;
//   };

//   const handleViewProfile = (uid) => {
//     navigate(`/profile/${uid}`);
//   };

//   const handleLike = async (likedUid) => {
//     if (!user || likedUsers.includes(likedUid)) return;

//     try {
//       // Add to likes collection
//       await addDoc(collection(db, "likes"), {
//         likerUid: user.uid,
//         likedUid: likedUid,
//         createdAt: serverTimestamp(),
//       });

//       // Update thread metadata
//       // const threadRef = doc(db, "messages", mid);
//       // await setDoc(
//       //   threadRef,
//       //   {
//       //     participants: [user.uid, likedUid],
//       //     lastText: `Someone liked your profile!`,
//       //     updatedAt: serverTimestamp(),
//       //   },
//       //   { merge: true }
//       // );

//       setLikedUsers((prev) => [...prev, likedUid]);
//       setSaveNotice({ type: "success", msg: "You've liked this profile!" });
//       setTimeout(() => setSaveNotice(null), 3000);
//     } catch (error) {
//       console.error("Error liking user:", error);
//       setSaveNotice({
//         type: "error",
//         msg: "There was an error liking this profile. Please try again.",
//       });
//       setTimeout(() => setSaveNotice(null), 3000);
//     }
//   };

//   const handleFavourite = async (favouritedUid) => {
//     if (!user) return;

//     try {
//       const q = query(
//         collection(db, "favourites"),
//         where("favouriterUid", "==", user.uid),
//         where("favouritedUid", "==", favouritedUid)
//       );
//       const snapshot = await getDocs(q);

//       if (snapshot.empty) {
//         await addDoc(collection(db, "favourites"), {
//           favouriterUid: user.uid,
//           favouritedUid: favouritedUid,
//           createdAt: serverTimestamp(),
//         });
//         setFavouritedUsers((prev) => [...prev, favouritedUid]);
//         setSaveNotice({
//           type: "success",
//           msg: "You've favourited this profile!",
//         });
//         setTimeout(() => setSaveNotice(null), 3000);
//       } else {
//         const docToDelete = snapshot.docs[0];
//         await deleteDoc(docToDelete.ref);
//         setFavouritedUsers((prev) =>
//           prev.filter((uid) => uid !== favouritedUid)
//         );
//         setSaveNotice({
//           type: "success",
//           msg: "You've unfavourited this profile.",
//         });
//         setTimeout(() => setSaveNotice(null), 3000);
//       }
//     } catch (error) {
//       console.error("Error updating favourite status:", error);
//       setSaveNotice({
//         type: "error",
//         msg: "There was an error updating your favourites. Please try again.",
//       });
//       setTimeout(() => setSaveNotice(null), 3000);
//     }
//   };

//   const applyFilters = () => {
//     if (myIsPremium) {
//       setFilters(form);
//     } else {
//       setFilters((f) => ({
//         ...f,
//         ageMin: form.ageMin,
//         ageMax: form.ageMax,
//         distanceKm: form.distanceKm,
//         role: "",
//         meetAt: "",
//         lookingFor: "",
//         onlineOnly: false,
//       }));
//     }
//   };
//   const resetFilters = () => {
//     setForm(defaultFilters);
//     setFilters(defaultFilters);
//   };

//   const ageOptions = [
//     { label: "Any", value: "Any" },
//     { label: "18–25", value: "18-25" },
//     { label: "26–35", value: "26-35" },
//     { label: "36–45", value: "36-45" },
//     { label: "46+", value: "46-99" },
//   ];

//   const distanceOptions = [5, 10, 25, 50, 100, 250, 500, 2000, 10000, 100000];

//   const formatDistance = (km) =>
//     km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;

//   return (
//     <div className="main discovery">
//       <div className="content">
//         {saveNotice && (
//           <div
//             role="status"
//             aria-live="polite"
//             className={`notice ${
//               saveNotice.type === "success" ? "notice-success" : "notice-error"
//             }`}
//             style={{
//               marginBottom: "12px",
//               padding: "10px 12px",
//               borderRadius: "8px",
//               background:
//                 saveNotice.type === "success"
//                   ? "rgba(16,185,129,0.15)"
//                   : "rgba(239,68,68,0.15)",
//               border: `1px solid ${
//                 saveNotice.type === "success"
//                   ? "rgba(16,185,129,0.6)"
//                   : "rgba(239,68,68,0.6)"
//               }`,
//             }}
//           >
//             {saveNotice.msg}
//           </div>
//         )}
//         {user ? (
//           <>
//             {/* Premium upsell modal for locked filters */}
//             <Modal
//               className="upgrade-prompt"
//               isOpen={showPremiumModal}
//               onRequestClose={() => setShowPremiumModal(false)}
//               contentLabel="Upgrade to unlock filters"
//             >
//               <>
//                 <h2>Unlock more filters</h2>
//                 <p>
//                   Sorting/filtering by Role, Meet At, Looking For, and Online
//                   Now is a Premium feature. Upgrade to refine your matches.
//                 </p>
//               </>
//               <div className="actions">
//                 <button onClick={() => setShowPremiumModal(false)}>
//                   Maybe later
//                 </button>
//                 <button
//                   onClick={() => {
//                     setShowPremiumModal(
//                       false
//                     ); /* navigate('/settings/upgrade') */
//                   }}
//                 >
//                   Upgrade
//                 </button>
//               </div>
//             </Modal>
//             {/* Filters Row */}
//             <section className="filters">
//               <div className="filters-inner">
//                 <div className="filter-icon" />

//                 <div className="filter-item">
//                   <label htmlFor="filter-age">Age</label>
//                   <select
//                     id="filter-age"
//                     name="age"
//                     value={`${form.ageMin}-${form.ageMax}`}
//                     onChange={(e) => {
//                       const v = e.target.value;
//                       if (!v || v === "Any") {
//                         setForm((f) => ({ ...f, ageMin: 18, ageMax: 99 }));
//                       } else {
//                         const [min, max] = v.split("-").map(Number);
//                         setForm((f) => ({ ...f, ageMin: min, ageMax: max }));
//                       }
//                     }}
//                   >
//                     {ageOptions.map((o) => (
//                       <option key={o.value} value={o.value}>
//                         {o.label}
//                       </option>
//                     ))}
//                   </select>
//                 </div>

//                 <div className="filter-item">
//                   <label htmlFor="filter-distance">Distance</label>
//                   <select
//                     id="filter-distance"
//                     name="distance"
//                     value={form.distanceKm}
//                     onChange={(e) =>
//                       setForm((f) => ({
//                         ...f,
//                         distanceKm: Number(e.target.value),
//                       }))
//                     }
//                   >
//                     {distanceOptions.map((km) => (
//                       <option key={km} value={km}>
//                         ≤ {km} km
//                       </option>
//                     ))}
//                   </select>
//                 </div>

//                 <div className="filter-item">
//                   <label htmlFor="filter-role">Role</label>
//                   <select
//                     id="filter-role"
//                     name="role"
//                     value={form.role}
//                     disabled={!myIsPremium}
//                     onChange={(e) => {
//                       if (!myIsPremium) {
//                         setShowPremiumModal(true);
//                         return;
//                       }
//                       setForm((f) => ({ ...f, role: e.target.value }));
//                     }}
//                   >
//                     <option value="">Any</option>
//                     <option value="top">Top</option>
//                     <option value="bottom">Bottom</option>
//                     <option value="verse">Verse</option>
//                     <option value="side">Side</option>
//                   </select>
//                 </div>

//                 <div className="filter-item">
//                   <label htmlFor="filter-meet">Meet At</label>
//                   <select
//                     id="filter-meet"
//                     name="meet_at"
//                     value={form.meetAt}
//                     disabled={!myIsPremium}
//                     onChange={(e) => {
//                       if (!myIsPremium) {
//                         setShowPremiumModal(true);
//                         return;
//                       }
//                       setForm((f) => ({ ...f, meetAt: e.target.value }));
//                     }}
//                   >
//                     <option value="">Any</option>
//                     <option value="yours">Yours</option>
//                     <option value="mine">Mine</option>
//                     <option value="outside">Outside</option>
//                     <option value="cafe">Cafe</option>
//                     <option value="bar">Bar</option>
//                     <option value="other">Other</option>
//                   </select>
//                 </div>

//                 <div className="filter-item">
//                   <label htmlFor="filter-looking">Looking For</label>
//                   <select
//                     id="filter-looking"
//                     name="looking_for"
//                     value={form.lookingFor}
//                     disabled={!myIsPremium}
//                     onChange={(e) => {
//                       if (!myIsPremium) {
//                         setShowPremiumModal(true);
//                         return;
//                       }
//                       setForm((f) => ({ ...f, lookingFor: e.target.value }));
//                     }}
//                   >
//                     <option value="">Any</option>
//                     <option value="friends">Friends</option>
//                     <option value="dating">Dating</option>
//                     <option value="casual">Casual</option>
//                     <option value="relationship">Relationship</option>
//                   </select>
//                 </div>

//                 <div className="filter-item">
//                   <label htmlFor="filter-online">Online Now</label>
//                   <select
//                     id="filter-online"
//                     name="online_now"
//                     value={form.onlineOnly ? "1" : ""}
//                     disabled={!myIsPremium}
//                     onChange={(e) => {
//                       if (!myIsPremium) {
//                         setShowPremiumModal(true);
//                         return;
//                       }
//                       setForm((f) => ({
//                         ...f,
//                         onlineOnly: e.target.value === "1",
//                       }));
//                     }}
//                   >
//                     <option value="">All</option>
//                     <option value="1">Online</option>
//                   </select>
//                 </div>

//                 <div className="filter-actions">
//                   <button
//                     className="btn-reset"
//                     type="button"
//                     onClick={resetFilters}
//                   >
//                     Reset
//                   </button>
//                   <button
//                     className="btn-apply"
//                     type="button"
//                     onClick={applyFilters}
//                   >
//                     Apply
//                   </button>
//                 </div>
//               </div>
//             </section>

//             {/* Results List */}
//             <section className="results-list">
//               {!locationInfo && locationError && !manualCoordinates && (
//                 <p className="notice">
//                   We couldn’t get your location. Enable permissions or search
//                   for a location manually.
//                 </p>
//               )}

//               {/* small counter */}
//               {/* View mode switcher */}
//               <div className="view-switcher">
//                 <button
//                   className={viewMode === "nearby" ? "active" : ""}
//                   onClick={() => setViewMode("nearby")}
//                 >
//                   Nearby
//                 </button>
//                 <button
//                   className={viewMode === "viewers" ? "active" : ""}
//                   onClick={() => setViewMode("viewers")}
//                 >
//                   Recent Views
//                 </button>
//                 <button
//                   className={viewMode === "my-likes" ? "active" : ""}
//                   onClick={() => setViewMode("my-likes")}
//                 >
//                   My Likes
//                 </button>
//                 <button
//                   className={viewMode === "favourites" ? "active" : ""}
//                   onClick={() => setViewMode("favourites")}
//                 >
//                   Favourites
//                 </button>
//                 <button
//                   className={viewMode === "most-liked" ? "active" : ""}
//                   onClick={() => setViewMode("most-liked")}
//                 >
//                   Most Liked
//                 </button>
//               </div>

//               {/* Time range selector for Most Liked */}
//               {viewMode === "most-liked" && (
//                 <div className="time-range-selector">
//                   <button
//                     className={timeRange === "week" ? "active" : ""}
//                     onClick={() => setTimeRange("week")}
//                   >
//                     This Week
//                   </button>
//                   <button
//                     className={timeRange === "month" ? "active" : ""}
//                     onClick={() => setTimeRange("month")}
//                   >
//                     This Month
//                   </button>
//                 </div>
//               )}

//               {/* Search bar for viewers */}
//               {viewMode === "viewers" && (
//                 <div className="viewer-search">
//                   <input
//                     type="text"
//                     placeholder="Search by name..."
//                     value={viewerSearch}
//                     onChange={(e) => setViewerSearch(e.target.value)}
//                   />
//                 </div>
//               )}

//               {/* Search bar for nearby */}
//               {viewMode === "nearby" && (
//                 <div className="viewer-search">
//                   <input
//                     type="text"
//                     placeholder="Search by name..."
//                     value={nearbySearch}
//                     onChange={(e) => setNearbySearch(e.target.value)}
//                   />
//                 </div>
//               )}

//               <p className="results-count">
//                 {viewMode === "nearby" &&
//                   (isLoading ? (
//                     <ClipLoader size={20} />
//                   ) : (
//                     <>
//                       Showing {nearestUsers.length} of {totalFiltered}
//                     </>
//                   ))}
//                 {viewMode === "viewers" &&
//                   (loadingViewers ? (
//                     <ClipLoader size={20} />
//                   ) : (
//                     <>Showing {recentViewers.length} recent viewers</>
//                   ))}
//                 {viewMode === "my-likes" &&
//                   (loadingMyLikes ? (
//                     <ClipLoader size={20} />
//                   ) : (
//                     <>Showing {myLikes.length} profiles that liked you</>
//                   ))}
//                 {viewMode === "favourites" &&
//                   (loadingFavourites ? (
//                     <ClipLoader size={20} />
//                   ) : (
//                     <>Showing {favourites.length} favourited profiles</>
//                   ))}
//                 {viewMode === "most-liked" &&
//                   (loadingMostLiked ? (
//                     <ClipLoader size={20} />
//                   ) : (
//                     <>Showing top {mostLikedUsers.length} profiles</>
//                   ))}
//               </p>

//               <div className="profile-row">
//                 {(viewMode === "nearby"
//                   ? nearestUsers
//                   : viewMode === "viewers"
//                   ? recentViewers
//                   : viewMode === "my-likes"
//                   ? myLikes
//                   : viewMode === "most-liked"
//                   ? mostLikedUsers
//                   : favourites
//                 ).map((u) => {

//                   const online = onlineUsersList?.[u?.uid]?.isOnline ?? false;
                  
                  
//                   return (
//                   <div
//                     className="profile-row-item"
//                     key={u.uid}
//                     onClick={() => handleViewProfile(u.uid)}
//                   >
//                     <div className="profile-image">
//                       <SafeMedia
//                         src={u.displayPhoto}
//                         alt={`${u.displayName}'s photo`}
//                         fallback="/img/placeholder.jpg"
//                       />
//                     </div>
//                     <div className="profile-info">
//                       <div className="user-info">
//                         <span
//                           className={`online-indicator ${
//                             online ? "is-online" : ""
//                           }`}
//                           aria-label={online ? "Online now" : "Offline"}
//                           title={online ? "Online now" : "Offline"}
//                         />
//                         <span className="name">{u.displayName}</span>
//                         <span className="age">{u.age}</span>
//                         {viewMode === "nearby" && u.distance != null && (
//                           <span className="distance">{` ${formatDistance(
//                             u.distance
//                           )} away`}</span>
//                         )}
//                         {viewMode === "viewers" && u.viewedAt && (
//                           <span className="viewed-at">
//                             {formatRelative(u.viewedAt)}
//                           </span>
//                         )}
//                         {viewMode === "my-likes" && u.likedAt && (
//                           <span className="liked-at">
//                             {formatRelative(u.likedAt)}
//                           </span>
//                         )}
//                         {viewMode === "most-liked" && (
//                           <span className="like-count">❤️ {u.likeCount}</span>
//                         )}
//                       </div>
//                       <div className="user-tags">
//                         <span className="tag pronouns">{u.pronouns}</span>
//                         <span className="tag sexuality">{u.sexuality}</span>
//                         <span className="tag role">{u.role}</span>
//                       </div>
//                     </div>
//                     <div
//                       className={`like-button ${
//                         likedUsers.includes(u.uid) ? "liked" : ""
//                       }`}
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         handleLike(u.uid);
//                       }}
//                     >
//                       ❤️
//                     </div>
//                     <div
//                       className={`favourite-button ${
//                         favouritedUsers.includes(u.uid) ? "favourited" : ""
//                       }`}
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         handleFavourite(u.uid);
//                       }}
//                     >
//                       ⭐
//                     </div>
//                   </div>
//                 )})}
//                 {viewMode === "most-liked" &&
//                   !loadingMostLiked &&
//                   mostLikedUsers.length === 0 && (
//                     <p className="notice">
//                       No liked profiles found for this period.
//                     </p>
//                   )}
//               </div>

//               {viewMode === "nearby" && totalFiltered > nearestUsers.length && (
//                 <button
//                   className="btn-load-more"
//                   type="button"
//                   onClick={() => setLimit((n) => n + 20)}
//                 >
//                   See More Profiles
//                 </button>
//               )}
//             </section>
//           </>
//         ) : (
//           <p>No profile found.</p>
//         )}
//       </div>
//     </div>
//   );
// };

// export default DiscoveryPage;
